// Debug utility to test shared watchlist queries
import { auth, db } from '../config/firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';

export async function debugSharedWatchlist() {
  try {
    console.log('🔍 Starting shared watchlist debug...');
    
    // Step 1: Get current user
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error('❌ Not authenticated');
      return;
    }
    console.log('✅ Current user:', currentUser.uid);

    // Step 2: Find a shared watchlist
    const sharedQuery = query(
      collection(db, 'sharedContent'),
      where('contentType', '==', 'watchlist'),
      where('isActive', '==', true)
    );
    const sharedSnapshot = await getDocs(sharedQuery);
    
    if (sharedSnapshot.empty) {
      console.log('❌ No shared watchlists found');
      return;
    }
    
    const sharedDoc = sharedSnapshot.docs[0];
    const sharedData = sharedDoc.data();
    console.log('✅ Found shared watchlist:', {
      id: sharedDoc.id,
      userId: sharedData.userId,
      ownerDisplayName: sharedData.ownerDisplayName
    });

    // Skip if it's the current user's own watchlist
    if (sharedData.userId === currentUser.uid) {
      console.log('⚠️ This is the current user\'s own watchlist, finding another...');
      const otherShared = sharedSnapshot.docs.find(doc => doc.data().userId !== currentUser.uid);
      if (!otherShared) {
        console.log('❌ No other users\' shared watchlists found');
        return;
      }
      sharedData.userId = otherShared.data().userId;
      sharedData.ownerDisplayName = otherShared.data().ownerDisplayName;
      console.log('✅ Found another user\'s watchlist:', sharedData.ownerDisplayName);
    }

    // Step 3: Try different queries
    console.log('\n📊 Testing queries for userId:', sharedData.userId);

    // Test A: Simple userId query
    console.log('\n🧪 Test A: Simple userId query...');
    try {
      const queryA = query(
        collection(db, 'watchlist'),
        where('userId', '==', sharedData.userId)
      );
      const snapshotA = await getDocs(queryA);
      console.log('✅ Query A success! Found', snapshotA.size, 'documents');
      
      if (snapshotA.size > 0) {
        const sampleDoc = snapshotA.docs[0];
        console.log('Sample doc:', {
          id: sampleDoc.id,
          symbol: sampleDoc.data().symbol,
          isShared: sampleDoc.data().isShared
        });
      }
    } catch (error: any) {
      console.error('❌ Query A failed:', error.code, error.message);
    }

    // Test B: userId + isShared query
    console.log('\n🧪 Test B: userId + isShared query...');
    try {
      const queryB = query(
        collection(db, 'watchlist'),
        where('userId', '==', sharedData.userId),
        where('isShared', '==', true)
      );
      const snapshotB = await getDocs(queryB);
      console.log('✅ Query B success! Found', snapshotB.size, 'documents');
    } catch (error: any) {
      console.error('❌ Query B failed:', error.code, error.message);
    }

    // Test C: Direct document access
    console.log('\n🧪 Test C: Direct document access...');
    try {
      // Try to access a specific document
      const testDocId = `${sharedData.userId}_AAPL`; // Common stock symbol
      const docRef = doc(db, 'watchlist', testDocId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        console.log('✅ Direct access to', testDocId, 'successful');
        console.log('Document data:', {
          symbol: docSnap.data().symbol,
          isShared: docSnap.data().isShared
        });
      } else {
        console.log('⚠️ Document', testDocId, 'does not exist');
      }
    } catch (error: any) {
      console.error('❌ Direct access failed:', error.code, error.message);
    }

    console.log('\n✨ Debug complete!');
    
  } catch (error) {
    console.error('❌ Debug error:', error);
  }
}

// Expose to window for console debugging
(window as any).debugSharedWatchlist = debugSharedWatchlist;